import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Copy, CheckCircle2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  createSharedLinkSchema,
  type CreateSharedLinkInput,
  EXPIRATION_PRESETS,
  type ExpirationPreset,
} from "@shared/validation/sharedLink.schema";
import { useCreateSharedLink } from "../../lib/api/useSharedLinks";
import { useChild } from "../../lib/api/useChildren";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { DialogShell } from "../ui/DialogShell";
import { Field } from "../ui/Field";
import { Button } from "../ui/Button";
import { VisibleSectionsPicker } from "./VisibleSectionsPicker";

const EXPIRATION_LABELS: Record<ExpirationPreset, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "30d": "30 days",
  never: "Never expires",
};

interface ShareLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId: string;
}

export function ShareLinkDialog({ open, onOpenChange, childId }: ShareLinkDialogProps) {
  const createMutation = useCreateSharedLink(childId);
  // Usually already cached by the profile page. Only used to say whether there
  // is a legal name to include; the server is what enforces the choice.
  const { data: child } = useChild(childId);
  const hasLegalName = Boolean(child?.legalName);
  const { publish } = useToast();
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateSharedLinkInput>({
    resolver: zodResolver(createSharedLinkSchema),
    defaultValues: {
      childId,
      caregiverName: "",
      caregiverEmail: null,
      visibleSections: {
        basic: true,
        emergencyContacts: false,
        allergies: false,
        medications: false,
        carePreferences: false,
        routines: false,
        feeding: false,
        medical: false,
        pickup: false,
        // Unticked by default, always: the legal name is an explicit opt-in.
        legalName: false,
      },
      expirationPreset: "7d",
    },
  });

  const onSubmit = async (data: CreateSharedLinkInput) => {
    try {
      const result = await createMutation.mutateAsync(data);
      const url = `${window.location.origin}/care/${result.token}`;
      setCreatedUrl(url);
      publish({
        variant: "success",
        title: "Share link created successfully",
        description: "Copy the link and send it to your caregiver.",
      });
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to create share link",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  const handleCopy = async () => {
    if (!createdUrl) return;
    try {
      await navigator.clipboard.writeText(createdUrl);
      setCopied(true);
      publish({ variant: "success", title: "Link copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      publish({ variant: "error", title: "Unable to copy link" });
    }
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setCreatedUrl(null);
      setCopied(false);
      reset();
      createMutation.reset();
    }
    onOpenChange(nextOpen);
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={handleClose}
      size="lg"
      title={createdUrl ? "Share Link Created!" : "Create New Share Link"}
      description={
        createdUrl
          ? "Copy this link and send it to your caregiver. It will only be shown once."
          : "Create a secure link to share your child's care information with a caregiver."
      }
    >
      {createdUrl ? (
        <div className="mt-6 space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="break-all font-mono text-xs text-emerald-900">{createdUrl}</p>
          </div>
          <Button className="w-full" onClick={handleCopy}>
            {copied ? (
              <>
                <CheckCircle2 className="h-4 w-4" aria-hidden /> Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" aria-hidden /> Copy link to clipboard
              </>
            )}
          </Button>
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-neutral-100 bg-white p-4 shadow-sm">
            <QRCodeSVG value={createdUrl} size={148} level="M" aria-label="QR code for the share link" />
            <p className="text-xs text-neutral-500">Or scan to open on a phone</p>
          </div>
          <p className="text-center text-xs text-neutral-500">
            For your security, the full link cannot be retrieved again. You can
            revoke access instantly from the list below.
          </p>
          <Button variant="secondary" className="w-full" onClick={() => handleClose(false)}>
            Done
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
          <Field
            label="Caregiver Name *"
            htmlFor="caregiver-name"
            error={errors.caregiverName?.message}
          >
            <input
              id="caregiver-name"
              type="text"
              {...register("caregiverName")}
              className={INPUT_CLASS}
              placeholder="e.g., Sarah Johnson (Babysitter)"
            />
          </Field>

          <Field
            label="Caregiver Email (Optional)"
            htmlFor="caregiver-email"
            error={errors.caregiverEmail?.message}
          >
            <input
              id="caregiver-email"
              type="email"
              {...register("caregiverEmail")}
              className={INPUT_CLASS}
              placeholder="sarah@example.com"
            />
          </Field>

          <Field label="Link Expires" htmlFor="expiration">
            <select id="expiration" {...register("expirationPreset")} className={INPUT_CLASS}>
              {EXPIRATION_PRESETS.map((preset) => (
                <option key={preset} value={preset}>
                  {EXPIRATION_LABELS[preset]}
                </option>
              ))}
            </select>
          </Field>

          <VisibleSectionsPicker register={register} />

          {/* BRIEF §5.2: the legal name is hidden on care links unless the
              parent ticks this. Disabled (and so never sent as true) when the
              profile has no legal name to include. */}
          <label
            className={
              hasLegalName
                ? "flex cursor-pointer items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-3 transition hover:border-neutral-300 hover:bg-neutral-50"
                : "flex cursor-not-allowed items-start gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-3"
            }
          >
            <input
              type="checkbox"
              {...register("visibleSections.legalName")}
              disabled={!hasLegalName}
              aria-describedby="include-legal-name-hint"
              className="mt-0.5 h-4 w-4 rounded border-neutral-300 accent-sky-600"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-neutral-900">
                Include legal name
              </span>
              <span id="include-legal-name-hint" className="block text-xs text-neutral-600">
                {hasLegalName
                  ? "Only if this caregiver may have to deal with a hospital or school. Otherwise they just see the nickname."
                  : "No legal name on this profile, so caregivers see the nickname only."}
              </span>
            </span>
          </label>

          {createMutation.error instanceof ApiError && (
            <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error.message}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting || createMutation.isPending}>
              {createMutation.isPending ? "Creating…" : "Create Share Link"}
            </Button>
          </div>
        </form>
      )}
    </DialogShell>
  );
}
